<template>
  <MasterModalBase
    width-xl
    overlay-scrollb
    :navigating="navDirection"
    :action-buttons="actionButtons"
    :main-transition-key="invoiceId"
    class="invoice-master-modal"
    :class="(isCorrection) ? 'modal-correction' : ''"
    @close-modal="close"
  >
    <template v-slot:title>
      <div v-if="!loading">
        <span v-if="invoice && !isCorrection">
          <span v-if="isRecurrent">
            {{ (stage === 'send') ? $gettext('Send') : '' }}
            {{ $gettext('Scheduled') }}
          </span>
          {{ modalTitle }} 
          <span v-if="!isRecurrent">{{ invoice.number }}</span>
        </span>
        <span v-if="invoice && isCorrection">{{ modalTitle }} {{ (!isCorrection) ? invoice.correction.name : '' }}</span>
        <span v-if="!invoice">{{ $gettext('New') }} {{ modalTitle }}</span>
        <span v-if="invoice && !isRecurrent && stage === 'view'" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" 
          :title="invoice && invoice.is_verified ? 
            $gettext('You have checked this invoice item. You can at any time remove this check by clicking here.') : 
            $gettext('You can check this invoice item to indicate that this entry has been checked and verified. It will then show a little checkmark next to it to remind you.')" 
          class="ml-2 text-info">

          <font-awesome-icon @click="verifyInvoice(invoice)" v-if="invoice && invoice.is_verified" icon="fa-solid fa-badge-check" />
          <font-awesome-icon @click="verifyInvoice(invoice)" v-if="invoice && !invoice.is_verified" icon="fa-regular fa-badge-check" />
        </span>
      </div>
      <div v-else style="min-height: 24px;" />
    </template>

    <template v-if="!hideModalTabs" v-slot:navbar>
      
      <!-- Tab: Add -->
      <div class="buttons-wrap" v-if="!invoiceIdSet" :class="[(stage === 'create') ? 'active' : '']">
        <div id="tt-invmodal-edit" class="buttons-tt-wrap">
          <GekkoButton
            v-if="!invoiceIdSet"
            :text="$gettext('Add')"
            :icon="'fa-regular fa-pen-to-square'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'create') ? 'active' : '']"
          />
        </div>
      </div>
      
      <!-- Tab: Edit non-recurrent -->
      <div class="buttons-wrap" v-if="!isRecurrent" :class="[(stage === 'edit') ? 'active' : '']">
        <div id="tt-invmodal-edit" class="buttons-tt-wrap">
          <GekkoButton
            v-if="invoiceIdSet"
            @click.prevent="loadView('edit')"
            :disabled="!invoiceIdSet || invoice && !isDraft && !isCorrection && !isUploaded"
            :text="$gettext('Edit')"
            :icon="'fa-regular fa-pen-to-square'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'edit') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!invoiceIdSet || invoice && !isDraft && !isCorrection && !isUploaded" target="tt-invmodal-edit" key="tt-invmodal-edit" placement="bottom" triggers="hover">
          <div class="tooltip-text">
            {{ $gettext('Editing is disabled for sent invoices') }}
          </div>
        </b-tooltip>
      </div>

      <!-- Tab: Edit recurrent -->
      <div class="buttons-wrap" v-if="isRecurrent" :class="[(stage === 'edit') ? 'active' : '']">
        <GekkoButton
          v-if="invoiceIdSet"
          :disabled="!invoiceIdSet"
          :text="$gettext('Edit')"
          :icon="'fa-regular fa-pen-to-square'"
          :variant="'modal-tab'"
          :class="['px-0', (stage === 'edit') ? 'active' : '']"
          v-gk-confirm="{
            title: $gettext('Edit recurrent invoice'),
            message: $gettext('This will remove your current planning and create a draft invoice that you can edit.'),
            options: {
              warning: $gettext('You would have to reinstate the planning if you want to send this invoice in the future.'),
              variant: 'primary',
              confirmText: $gettext('Ok'),
              safeguard: true,
              safeguardText: $gettext('Delete automatic sending task')
            },
            callback: () => { editRecurrentInvoice() }
          }"
        />
      </div>
      <!-- Tab: View -->
      <div class="buttons-wrap" :class="[(stage === 'view') ? 'active' : '']">
        <div id="tt-invmodal-view" class="buttons-tt-wrap">
          <GekkoButton
            @click.prevent="loadView('view')"
            :disabled="!invoiceIdSet"
            :text="$gettext('Overview')"
            :icon="'fa-regular fa-eye'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'view') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!invoiceIdSet" target="tt-invmodal-view" key="tt-invmodal-view" placement="bottom" triggers="hover">
          <div class="tooltip-text">
            {{ $gettext('Invoice is not saved') }}
          </div>
        </b-tooltip>
      </div>


      <!-- Tab: Email/Send -->
      <div class="buttons-wrap" v-if="!isCorrection" :class="[(stage === 'send') ? 'active' : '']">
        <div id="tt-invmodal-send" class="buttons-tt-wrap">
          <GekkoButton
            @click.prevent="loadView('send')"
            :disabled="!invoiceIdSet || (isRecurrent)"
            :text="$gettext('Email')"
            :icon="'fa-regular fa-paper-plane'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'send') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!invoiceIdSet || (isRecurrent && !scheduledForToday)" target="tt-invmodal-send" key="tt-invmodal-send" placement="bottom" triggers="hover">
          <div v-if="!isRecurrent" class="tooltip-text">
            {{ $gettext('Invoice is not saved') }}
          </div>
        </b-tooltip>
      </div>

      <!-- Tab: Payments -->
      <div class="buttons-wrap" v-if="!isCorrection" :class="[(stage === 'payments') ? 'active' : '']">
        <div id="tt-invmodal-payments" class="buttons-tt-wrap">
          <GekkoButton
            @click.prevent="loadView('payments')"
            :disabled="!invoiceIdSet || invoice && (isDraft || isRecurrent)"
            :text="$gettext('Payment')"
            :icon="'fa-regular fa-credit-card'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'payments') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!invoiceIdSet || invoice && (isDraft || isRecurrent)" target="tt-invmodal-payments" key="tt-invmodal-payments" placement="bottom" triggers="hover">
          <div v-if="!isRecurrent" class="tooltip-text">
            {{ $gettext('Before this invoice is sent, you can only add payment on the edit page') }}
          </div>
        </b-tooltip>
      </div>

    </template>
    
    <template v-slot:maincontent>
      <div class="master-invoice-modal-view-wrap" :class="{ 'overflow-hidden': (stage === 'view' && !isCorrection && !isUploaded) }">
        <EInvoiceMessageModal 
          :showModal="showEInvoiceMessageModal"
          :title="eInvoiceMessageTitle"
          :message="eInvoiceMessageText"
          :showAnimation="eInvoiceShowAnimation"
          :actionType="eInvoiceMsgActionType"
          :recurrentId="recurrentInvoiceId"
          :searchedCompanies="eInvoiceSearchedCompanyList"
          @close="onEInvoiceMessageClose"
          @selectedCompany="onPeppolCompanySelected"
        />
        <LoadingSpinner v-if="loading" />
        <div v-else class="master-invoice-modal-view">
          <div v-if="stage === 'create'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceAdd
              ref="invoice-master-add"
              @display-buttons="showActionButtons"
              @invoice-updated="onInvoiceCreated"
            />
          </div>
          <div v-if="stage === 'edit'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceAdd
              v-if="!isCorrection && !isUploaded"
              is-edit
              ref="invoice-master-add"
              @display-buttons="showActionButtons"
              @invoice-updated="onInvoiceUpdated"
            />
            <EditCorrectionView
              v-if="isCorrection"
              ref="correction-master-edit"
              @display-buttons="showActionButtons"
              @correction-updated="onCorrectionUpdated"
            />
            <EditInvoiceUploadView
              v-if="isUploaded"
              ref="invoice-uploaded-master-edit"
              @display-buttons="showActionButtons"
              @invoice-updated="onInvoiceUpdated"
            />
          </div>
          <div v-if="stage === 'view' && !isCorrection && !isUploaded" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceView
              ref="invoice-master-view"
              @refresh-list="setListRefresh"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
              @mark-as-sent="_markInvoiceAsSent"
            />
          </div>
          <div v-if="stage === 'view' && isUploaded" class="view-wrap" :class="['view-wrap--' + stage]">
            <UploadedInvoiceView
              ref="uploaded-invoice-master-view"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
              @mark-as-sent="_markInvoiceAsSent"
            />
          </div>
          <div v-if="stage === 'view' && isCorrection" class="view-wrap" :class="['view-wrap--' + stage]">
            <CorrectionView
              ref="correction-master-view"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
              @mark-as-sent="_markInvoiceAsSent"
            />
          </div>
          <div v-if="stage === 'send'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceSend
              ref="invoice-master-send"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
              @invoice-error-message="showInvoiceErrorMessage"
              @mark-as-sent="_markInvoiceAsSent"
            />
          </div>
          <div v-if="stage === 'payments'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoicePayments
              ref="invoice-master-payments"
              :invoice="invoice" 
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
              :key="invoiceId"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-if="navDirection !== 'no-transition' && invoice && stage === 'view' && invoice.status !== 'recurrent' && actionButtons && actionButtons != ''" v-slot:prevnext>
      <PrevNextNavInvoices :item-id="invoiceId" @nav-direction="(dir) => { navDirection = dir }" />
    </template>

    <!-- ACTION BUTTONS -->
    <template v-if="actionButtons && actionButtons != ''" v-slot:actions>
      <div class="bottom-action-btns--wrap">

        <!-- VIEW INVOICE -->
        <template v-if="invoice && actionButtons === 'view'">
          <!-- payments -->
          <GekkoButton
            v-if="(invoice.status === 's' || invoice.status === 'p') && !isCorrection"
            :to="{ name: !isControlPanel ? 'InvoicePayments' : 'ControlPanelInvoicePayments', params: { invoiceId: invoiceId } }"
            :text="(invoice.status === 's') ? $gettext('Add payment') : $gettext('View payments')"
            icon="fa-regular fa-credit-card"
            :variant="(invoice.status === 'p') ? 'outline-primary' : 'primary'"
          />

          <!-- schedule invoice -->
          <div class="premium-badge--inline" style="position: relative;">
            <GekkoButton
              v-if="(invoice.status === 'd') && !isCorrection"
              @click="toggleRecurrentModal()"
              :text="$gettext('Schedule')"
              variant="outline-primary"
              icon="fa-regular fa-clock-rotate-left"
              :loading="isWaitingForResponse"
            >
              <template v-slot:premium-badge>
                <GkBadge 
                  icon="fa-solid fa-crown"
                  icon-only
                  type="premium"
                  variant="light-ghost"
                />
              </template>
            </GekkoButton>
          </div>

          <!-- send -->
          <div style="position: relative;">
            <GekkoButton 
              v-if="(invoice && invoice.status === 'd' && !isCorrection)"
              :to="{ name: !isControlPanel ? 'InvoiceSend' : 'ControlPanelInvoiceSend', params: { invoiceId: invoiceId } }"
              :text="$gettext('Send')"
              icon="fa-regular fa-paper-plane"
              :loading="isWaitingForResponse"
            />
          </div>
        </template>
        <!-- VIEW RECURRENT INVOICE -->
        <template v-if="invoice && actionButtons === 'view-recurrent'">
          <!-- edit schedule invoice -->
          <GekkoButton
            v-if="invoice.status === 'recurrent'"
            @click="$store.commit('invoicesRecurrent/TOGGLE_MODAL')"
            :text="$gettext('Edit Schedule')"
            variant="outline-primary"
            icon="fa-regular fa-clock-rotate-left"
          />
        </template>

        <!-- SEND INVOICE -->
        <template v-if="invoice && !isRecurrent && actionButtons === 'send' && $refs['invoice-master-send']">
          <!-- mark as sent -->
          <GekkoButton 
            v-if="invoice.status === 'd'"
            @click="handleMarkAsSent()"
            :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
            :text="$gettext('Mark as sent')"
            icon="fa-regular fa-envelope-circle-check"
            variant="outline-primary"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
          <!-- send invoice -->
          <GekkoButton 
            v-if="invoice.status === 'd'"
            @click="handleAdjustAndSend()"
            :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
            :text="$gettext('Send')"
            icon="fa-regular fa-paper-plane"
            type="submit"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
          <!-- send reminder -->
          <GekkoButton
            v-else
            @click="$refs['invoice-master-send'].adjustAndSend()"
            :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
            :text="$gettext('Send reminder')"
            icon="fa-regular fa-paper-plane"
            type="submit"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </template>

        <!-- SEND RECURRENT INVOICE -->
        <template v-if="invoice && actionButtons === 'send-recurrent' && $refs['invoice-master-send']">
          <!-- send recurrent -->

          <GekkoButton 
            v-if="invoice.status === 'recurrent'"
            @click="handleAdjustAndSend()"
            :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
            :text="$gettext('Send invoice')"
            icon="fa-regular fa-paper-plane"
            type="submit"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </template>

        <!-- EDIT CORRECTION -->
        <template v-if="actionButtons === 'create' && isCorrection">
          <!-- update correction -->
          <GekkoButton
            :text="$gettext('Update correction')"
            icon="fa-regular fa-floppy-disk"
            variant="primary"
            @click="$refs['correction-master-edit'].saveCorrection()"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </template>

        <!-- EDIT UPLOADED INVOICE -->
        <template v-if="actionButtons === 'create' && isUploaded">
          <!-- update correction -->
          <GekkoButton
            :text="$gettext('Update invoice')"
            icon="fa-regular fa-floppy-disk"
            variant="primary"
            @click="$refs['invoice-uploaded-master-edit'].saveUploadedInvoice()"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </template>

        <!-- ADD/EDIT INVOICE -->
        <template v-if="actionButtons === 'create' && !isCorrection && !isUploaded">
          <!-- schedule summary -->
          <div v-if="scheduleSummary" class="recurrent-summary" @click="$store.commit('invoicesRecurrent/TOGGLE_MODAL')">
            <font-awesome-icon icon="fa-regular fa-clock-rotate-left" />
            {{ scheduleSummary }}
          </div>
          <!-- save invoice -->
          <GekkoButton
            id="invoices_web_new_save_button"
            v-if="$refs['invoice-master-add']"
            @click="$refs['invoice-master-add'].saveInvoice()"
            :text="$gettext('Save')"
            icon="fa-regular fa-floppy-disk"
            variant="primary"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </template>

        <!-- PAYMENTS -->
        <template v-if="actionButtons === 'payments'">
          <GekkoButton
            v-if="$refs['invoice-master-payments'] && $refs['invoice-master-payments'].isPaid"
            :to="{ name: !isControlPanel ? 'InvoiceView' : 'ControlPanelInvoiceView', params: { invoiceId: invoiceId } }"
            :text="$gettext('View invoice')"
            icon="fa-regular fa-eye"
            variant="outline-primary"
          />
          <GekkoButton
            v-if="$refs['invoice-master-payments'] && !$refs['invoice-master-payments'].isPaid"
            @click="$refs['invoice-master-payments'].openPaymentsModal()"
            :disabled="!$refs['invoice-master-payments'].isPaymentAllowed"
            :text="$gettext('Add payment')"
            icon="fa-solid fa-plus"
            variant="primary"
          />
        </template>

        <!-- HISTORY -->
        <template v-if="actionButtons === 'history'">
          <GekkoButton
            :to="{ name: !isControlPanel ? 'InvoiceView' : 'ControlPanelInvoiceView', params: { invoiceId: invoiceId } }"
            :text="$gettext('View invoice')"
            icon="fa-regular fa-eye"
            variant="primary"
          />
        </template>

        <!-- MORE ACTIONS DROPDOWN -->
        <div v-if="invoice && stage === 'view' && invoice.status !== 'recurrent'">
          <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" :key="'options-' + invoice.id" variant="outline-primary" right no-caret dropup>
            <template #button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
            </template>
            <b-dropdown-item @click="downloadInvoice(invoice.id)" :title="$gettext('download invoice')">
              <font-awesome-icon icon="fa-regular fa-download" />
              <span v-translate>Download</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="!isCorrection && !isUploaded" @click="downloadInvoiceUbl(invoice.id)" :title="$gettext('UBL (Universal Business Language) is a European standard invoicing format used by various accounting software.')">
              <font-awesome-icon icon="fa-regular fa-download" />
              <span v-translate>Download UBL</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="!invoice.uploaded && !isCorrection" @click="setInvoiceToCopy(invoice)" class="premium-badge--inline">
              <font-awesome-icon icon="fa-regular fa-copy" />
              <span v-translate>Copy invoice</span>
              <GkBadge 
                icon="fa-solid fa-crown"
                icon-only
                type="premium"
              />
            </b-dropdown-item>
            <b-dropdown-item v-if="stage === 'view' && invoice.status === 'd' && !isCorrection" :to="{ name: !isControlPanel ? 'InvoiceSend' : 'ControlPanelInvoiceSend', params: { invoiceId: invoiceId } }">
              <font-awesome-icon icon="fa-regular fa-paper-plane" />
              <span v-translate>Send</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="stage === 'view' && invoice.status !== 'd' && !isCorrection" :to="{ name: !isControlPanel ? 'InvoiceSend' : 'ControlPanelInvoiceSend', params: { invoiceId: invoiceId } }">
              <font-awesome-icon icon="fa-regular fa-paper-plane" />
              <span v-translate>Send reminder</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="stage === 'view' && invoice.status === 'd'" @click="handleMarkAsSent()">
              <font-awesome-icon icon="fa-regular fa-envelope-circle-check" />
              <span v-translate>Mark as sent</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="stage !== 'edit' && (invoice.status === 'd' || isCorrection || isUploaded)" :to="{ name: !isControlPanel ? 'InvoiceEdit' : 'ControlPanelInvoiceEdit', params: { invoiceId: invoiceId } }">
              <font-awesome-icon icon="fa-regular fa-pen" />
              <span v-translate>Edit</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="!isCorrection && !isUploaded && invoice.display_status && creditableStatuses.includes(invoice.display_status.detailed_status)" @click="creditInvoice(invoice.id)">
              <font-awesome-icon icon="fa-regular fa-rotate-left" />
              <span v-translate>Credit invoice</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="!isControlPanel && stage === 'view' && invoice.status === 'd'" @click="$refs['invoice-master-view'].showDesign = true" :title="$gettext('Invoice design')" class="premium-badge--inline">
              <font-awesome-icon icon="fa-regular fa-palette" />
              <span v-translate>Design your invoice</span>
              <GkBadge 
                icon="fa-solid fa-crown"
                icon-only
                type="premium"
              />
            </b-dropdown-item>
            <DropdownItemDelete
              v-if="showDropdownItemDelete"
              :key="'dd-delete-' + invoice.id"
              :item="invoice"
              :item-type="'invoice'"
              @item-deleted="onInvoiceDelete"
            />
          </b-dropdown>
        </div>

      </div>
    </template> <!-- action buttons -->

  </MasterModalBase>
</template>
  
<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import { promptDownload } from "@/utils/"

import invoices from "@/mixins/invoices"

import InvoiceAdd from '@/components/invoices/InvoiceAdd'
import InvoiceView from '@/components/invoices/InvoiceView'
import InvoiceSend from '@/components/invoices/InvoiceSend'
import InvoicePayments from '@/components/invoices/InvoicePayments'
import CorrectionView from '@/components/invoices/CorrectionView'
import UploadedInvoiceView from '@/components/invoices/UploadedInvoiceView'

import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';

import EditCorrectionView from "@/components/invoices/EditCorrectionView"
import EditInvoiceUploadView from "@/components/invoices/EditInvoiceUploadView"
import EInvoiceMessageModal from "@/components/invoices/ui/EInvoiceMessageModal.vue"
import GekkoButton from "@/components/GekkoButton"
import MasterModalBase from "@/components/base_components/MasterModalBase"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DropdownItemDelete from "@/components/ui/DropdownItemDelete"
import StatusBadge from "@/components/invoices/ui/StatusBadge"
import PrevNextNavInvoices from "@/components/invoices/ui/PrevNextNavInvoices"
import InvoiceService from "@/store/services/invoice.service.js"

export default {
  name: "InvoiceMasterModal",
  mixins: [invoices],
  components: {
    MasterModalBase,
    GekkoButton,
    LoadingSpinner,
    InvoiceView,
    InvoiceAdd,
    InvoiceSend,
    InvoicePayments,
    DropdownItemDelete,
    StatusBadge,
    EditCorrectionView,
    EditInvoiceUploadView,
    PrevNextNavInvoices,
    CorrectionView,
    UploadedInvoiceView,
    EInvoiceMessageModal
  },
  props: {
    stage: {
      type: String,
      default: null
    },
    invoiceId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      loading: true,
      actionButtons: null,
      refreshList: false,
      navDirection: null,

      // Variables for e-invoice flow
      showEInvoiceMessageModal: false,
      eInvoiceMessageTitle: '',
      eInvoiceMessageText: '',
      eInvoiceMsgActionType: null,
      eInvoiceShowAnimation: null,
      eInvoiceParams: null,
      eInvoiceMarkAsSent: false,
      eInvoiceSearchedCompanyList: [],
      markAsSentDetails: {},
      recurrentInvoiceId: null
    }
  },
  computed: {
    ...mapGetters({ 
      creditableStatuses: 'invoices/getCreditableStatuses',
      invoice: 'invoices/getInvoice',
      recurrentInvoice: 'invoices/getRecurrentInvoice',
      scheduleSummary: 'invoicesRecurrent/getSummary',
      getRefreshCustomerList: 'invoices/getRefreshCustomerList',
      scheduledForToday: "invoices/getScheduledForToday",
      isWaitingForResponse: 'main/isWaitingForResponse',
      getCompanyId: 'main/getCompanyId',
      currentCompany: "main/getCurrentCompany",
      isControlPanel: "main/isControlPanel"
    }),
    hideModalTabs () {
      return this.isCorrection || (this.isRecurrent && this.scheduledForToday && (this.stage === 'send'))
    },
    modalTitle () {
      let modal_title = this.$gettext('Invoice')
      if (this.isCorrection) {
        modal_title = this.$gettext('Correction')
      }
      return modal_title
    },
    invoiceIdSet () {
      return (this.invoiceId && this.invoiceId != '')
    },
    isCorrection () {
      return (this.invoice && this.invoice.correction !== null)
    },
    isRecurrent () {
      return (this.invoice && this.invoice.status === 'recurrent')
    },
    isDraft () {
      return (this.invoice && this.invoice.status === 'd')
    },
    isUploaded () {
      return (this.invoice && this.invoice.uploaded)
    },
    showDropdownItemDelete() {
      return (this.invoice && !this.invoice.verifactuinvoice) || 
        (this.invoice && this.invoice.verifactuinvoice && ["draft", "unmarked", "unmarked_sent", "incorrect", "error"].includes(this.invoice.verifactuinvoice.status))
    }
  },
  watch: {
    invoiceId () {
      this.$nextTick(() => {
        // reset recurrent data because it can be set from outside in RecurrentInvoiceModal
        this.resetInvoiceData()
        this.resetSummary()
        this.setupData()
      })
    }
  },
  created () {
    this.resetSummary()
    this.setupData()
  },
  methods: {
    ...mapMutations({
      setRefreshCustomerList: 'invoices/SET_REFRESH_CUSTOMER_LIST',
      openRecurrentModal: 'invoicesRecurrent/TOGGLE_MODAL',
      setStoreSelectedBrand: 'brands/SET_SELECTED_BRAND',
      resetCustomers: "customers/RESET_STATE",
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    ...mapActions({ 
      loadInvoice: "invoices/loadInvoice",
      loadInvoiceRecurrent: "invoices/loadInvoiceRecurrent",
      loadCorrectionFiles: "invoices/loadCorrectionFiles",
      setInvoiceToCopy: "invoices/setInvoiceToCopy",
      resetInvoice: 'invoices/resetInvoice',
      resetInvoiceData: 'invoices/resetInvoiceData',
      resetSummary: 'invoicesRecurrent/resetSummary',
      loadInvoiceCustomers: 'invoices/loadInvoiceCustomers',

      recurrentToDraft: "invoices/recurrentToDraft",
      createInvoice: "invoices/createInvoice",
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber",

      resetCustomerList: 'customers/resetCustomerList'
    }),
    close () {
      this.resetInvoice()
      this.resetInvoiceData()
      this.resetSummary()
      if (this.refreshList) {
        this.resetCustomerList('invoices')
        this.$store.commit('invoices/REFRESH_INVOICE_LIST')
      }
      if (this.getRefreshCustomerList) {
        this.loadInvoiceCustomers().then(() => {
          this.setRefreshCustomerList(false)
        })
      }
      this.setStoreSelectedBrand(null)
      this.$emit('close')
      if (this.isControlPanel) {
        this.$router.push({ name: 'ControlPanelInvoices', params: { customerId: this.getCompanyId } }).catch(() => {})
      } else {
        this.$router.push({ name: 'Invoices' }).catch(() => {})
      }
    },
    onInvoiceDelete () {
      this.refreshList = true
      this.close()
    },
    loadView (view) {
      switch (view) {
        case 'create':
          this.$router.push({ name: !this.isControlPanel ? 'InvoiceAdd' : 'ControlPanelInvoiceAdd' }).catch(() => {})
          break;
        case 'edit':
          this.$router.push({ name: !this.isControlPanel ? 'InvoiceEdit' : 'ControlPanelInvoiceEdit', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
        case 'view':
          if (this.isRecurrent) {
            this.$router.push({ name: !this.isControlPanel ? 'InvoiceViewRecurrent' : 'ControlPanelInvoiceViewRecurrent', params: { invoiceId: this.invoiceId } }).catch(() => {})
          } else {
            this.$router.push({ name: !this.isControlPanel ? 'InvoiceView' : 'ControlPanelInvoiceView', params: { invoiceId: this.invoiceId } }).catch(() => {})
          }
          break;
        case 'send':
          this.$router.push({ name: !this.isControlPanel ? 'InvoiceSend' : 'ControlPanelInvoiceSend', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
        case 'payments':
          this.$router.push({ name: !this.isControlPanel ? 'InvoicePayments' : 'ControlPanelInvoicePayments', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
        case 'history':
          this.$router.push({ name: !this.isControlPanel ? 'InvoiceHistory' : 'ControlPanelInvoiceHistory', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
      }
    },
    setupData () {
      if (this.stage !== 'create' && this.invoiceId) {
        this.loading = true
        const fnName = (this.$route.meta.recurrent) ? 'loadInvoiceRecurrent': 'loadInvoice'
        // load invoice (or recurrent)
        this[fnName](this.invoiceId).then(() => {

          if (this.isRecurrent) {
            if (!this.recurrentInvoice.active) {
              if (this.isControlPanel) {
                this.$router.push({ name: 'ControlPanelInvoices', params: { customerId: this.getCompanyId } }).catch(() => {})
              } else {
                this.$router.push({ name: 'Invoices' }).catch(() => {})
              }
              this.$store.commit('setAlert', {
                title: this.$gettext('Not found'),
                text: this.$gettext('The recurrent invoice you are trying to open does not exist'),
                variant: 'warning',
                duration: 4
              })
            } else {
              if (this.stage === 'view' && this.$route.query.scheduler) {
                this.openRecurrentModal()
              }
            }
          }
          
          if (this.invoice.correction) {
            // load correction files
            this.loadCorrectionFiles(this.invoice.correction.id).then(() => {
              this.loading = false
            })
          } else {
            this.loading = false
          }
        }).catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Not found'),
            text: this.$gettext('The invoice you are trying to open does not exist'),
            variant: 'warning',
            duration: 4
          })
          if (this.isControlPanel) {
            this.$router.push({ name: 'ControlPanelInvoices', params: { customerId: this.getCompanyId } }).catch(() => {})
          } else {
            this.$router.push({ name: 'Invoices' }).catch(() => {})
          }
        })
      } else {
        this.loading = false
      }
    },
    showActionButtons (payload) {
      this.actionButtons = null // set to null to trigger change (for payments)
      this.$nextTick(() => {
        this.actionButtons = payload
      })
    },
    onInvoiceCreated () {
      this.$store.commit('setAlert', {
        title: this.$gettext('Invoice created'),
        text: this.$gettext('The invoice was created successfully'),
        variant: 'primary',
        duration: 4
      })
      this.navDirection = 'no-transition'
      this.setListRefresh()
    },
    onInvoiceUpdated () {
      this.$store.commit('setAlert', {
        title: this.$gettext('Invoice updated'),
        text: this.$gettext('The invoice was edited successfully'),
        variant: 'primary',
        duration: 4
      })
      this.setListRefresh()
    },
    downloadInvoiceUbl(invoice_id){
      InvoiceService.getInvoiceUBLBlob(invoice_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    downloadInvoice(invoice_id) {
      InvoiceService.getInvoicePDFBlob(invoice_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    onCorrectionUpdated () {
      this.$store.commit('setAlert', {
        title: this.$gettext('Correction updated'),
        text: this.$gettext('The correction was edited successfully'),
        variant: 'primary',
        duration: 4
      })
      this.setListRefresh()
    },
    setListRefresh () {
      this.setupData()
      this.refreshList = true
    },
    editRecurrentInvoice () {
      /**
       * 1. delete recurrent task
       * 2. create a new one with same data
       * 3. redirect to new draft invoice id
       */

      const recurrent_task = Object.assign({}, this.recurrentInvoice)
      recurrent_task.active = false
      recurrent_task.company_profile = this.currentCompany.id
      recurrent_task.created_by = this.recurrentInvoice.created_by.id;

      if (this.recurrentInvoice.days) {
        if (this.recurrentInvoice.days.length == 0) {
          recurrent_task.days = ''
        } else {
          recurrent_task.days = this.recurrentInvoice.days.join()
        }
      }
      recurrent_task.invoice = this.recurrentInvoice.invoice.id

      // setting recurrent task to inactive
      InvoiceRecurrentService.update(recurrent_task.id, recurrent_task).then(() => {
        
        this.$store.commit('invoices/REFRESH_INVOICE_RECURRENT_LIST')

        const prepared_new_invoice = Object.assign({}, this.invoice)

        const data = { 'sender_company': this.currentCompany.id, 'year': new Date().format('Y') }
        this.getNextInvoiceNumber(data).then((next_number) => {
          prepared_new_invoice.number = next_number
          prepared_new_invoice.status = 'd'
          prepared_new_invoice.invoice_date = new Date().format('Y-m-d')

          this.createInvoice(prepared_new_invoice).then((new_invoice) => {
            console.log('recurrentInvoice still is', this.recurrentInvoice)
            console.log('this.createInvoice', new_invoice)

            this.$store.commit('invoices/SET_RECURRENT_INVOICE_BACKUP', this.recurrentInvoice)

            this.$store.commit('invoices/REFRESH_INVOICE_LIST')
            this.$router.push({ name: !this.isControlPanel ? 'InvoiceEdit' : 'ControlPanelInvoiceEdit', params: { invoiceId: new_invoice.id } })
          })
        })
      })

    },

    // Methods for e-invoice flow + mark as sent
    onEInvoiceMessageClose() {
      this.showEInvoiceMessageModal = false
      this.eInvoiceMessageTitle = ''
      this.eInvoiceMessageText = ''
      this.eInvoiceShowAnimation = null
      this.eInvoiceMsgActionType = null
      this.eInvoiceParams = null
      this.eInvoiceSearchedCompanyList = []
      this.eInvoiceMarkAsSent = false
      this.showAdjustModal = false
      this.recurrentInvoiceId = null
      this.setWaitingForResponse(false)
    },
    showInvoiceErrorMessage(error) {
      if (error.status === 491 && this.invoice.verifactuinvoice) this.eInvoiceMsgActionType = 'upload_certificate'
      else if (error.status === 491 && this.invoice.peppolinvoice.status) this.eInvoiceMsgActionType = 'connect_peppol'
      else if (error.status === 493) this.eInvoiceMsgActionType = 'edit_company_profile'
      else if (error.status === 492) this.eInvoiceMsgActionType = 'edit_invoice'
      else this.eInvoiceMsgActionType = 'redirect_invoice' // recurrent invoice error

      if (error.recurrentInvoiceId) this.recurrentInvoiceId = error.recurrentInvoiceId

      this.showEInvoiceMessageModal = true
      this.eInvoiceMessageTitle = this.$gettext('Electronic invoice not sent')
      this.eInvoiceMessageText = error.data.detail
      this.eInvoiceShowAnimation = error.status === 491 ? 'lock' : 'error'
      this.loadInvoice(this.invoice.id)
    },
    _handlePeppolSelection(params=null, markAsSent=false) {
      const invoiceId = this.$route.params['invoiceId']
      this.setWaitingForResponse(true)
      this.eInvoiceMarkAsSent = markAsSent

      InvoiceService.checkPeppolReceiver(invoiceId).then((response) => {
        this.showEInvoiceMessageModal = true
        this.eInvoiceShowAnimation = null
        this.eInvoiceMsgActionType = 'confirmation'
        this.eInvoiceParams = params
        
        this.eInvoiceSearchedCompanyList = response.data.map(company => {
          return {
            eaid_name: company.identifier ? company.identifier.eaid_name : '',
            eaid_code: company.identifier ? company.identifier.eaid_code : '',
            endpoint_id: company.identifier ? company.identifier.endpoint_id : '',
            eia: company.eia,
            name: company.participant.name
          }
        })

        this.eInvoiceMessageTitle = this.$gettext('Choose invoice receiver')
        this.eInvoiceMessageText = this.$gettext('The following Peppol receiver(s) were found for this customer. Select the receiver you want to send the invoice to, or search for a company with a different name. Click Continue to use your own customer information.')
      }).catch((error) => {
        this.showEInvoiceMessageModal = true
        this.eInvoiceShowAnimation = null
        this.eInvoiceMsgActionType = 'confirmation'
        this.eInvoiceParams = params

        if (error.status === 404) {
          this.eInvoiceMessageTitle = this.$gettext('Invoice receiver not found')
          this.eInvoiceMessageText = this.$gettext('We could not find any company registered in Peppol that matches your customer name. Search for a company with a different name, or click Continue to use your own customer information.')
        } else if (error.status === 413) {
          this.eInvoiceMessageTitle = this.$gettext('Too many invoice receivers found')
          this.eInvoiceMessageText = this.$gettext('Your customer company name is too generic and therefore multiple Peppol receivers were found. Please search for a company with a different name, or click Continue to use your own customer information.')
        } else {
          this.eInvoiceMessageTitle = this.$gettext('Error retrieving receiver information')
          this.eInvoiceMessageText = this.$gettext('An unexpected error occurred while retrieving the Peppol receiver information. Do you still want to send the invoice?')
        }
      }).finally(() => {
        this.setWaitingForResponse(false)
      })
    },
    _markInvoiceAsSent () {
      this.loading = true
      this.setWaitingForResponse(true)

      InvoiceService.markAsSent(this.invoice.id, this.markAsSentDetails).then(() => {
        this.loadInvoice(this.invoice.id)
        this.$store.commit('invoices/REFRESH_INVOICE_LIST')
        this.showAdjustModal = false
        this.$router.push({ name: !this.isControlPanel ? 'InvoiceView' : 'ControlPanelInvoiceView', params: { invoiceId: this.invoice.id } }).catch(() => {})
      }).catch((error) => {
        if (error && [490, 491, 492, 493].includes(error.status)) {
          this.showInvoiceErrorMessage(error)
        } else {
          this.$store.commit('setAlert', {
            title: this.$gettext("Invoice not marked as sent"),
            text: this.$gettext("An error occurred while attempting to mark the invoice as sent."),
            variant:"danger",
            duration: 4
          })
        }
      }).finally(() => {
        this.loading = false
        this.setWaitingForResponse(false)
      })
    },
    onPeppolCompanySelected(selectedCompany) {
      if (this.eInvoiceMarkAsSent) {
        this.markAsSentDetails = {
          peppol_details: selectedCompany
        }
        if (this.stage === 'view') { // uploaded invoice not available on Peppol
          this.$refs['invoice-master-view'].markAsSent()
        } else {
          this.$refs['invoice-master-send'].markAsSent()
        }
      } else {
        this.$refs['invoice-master-send'].sendDetails.peppol_details = selectedCompany
        this.$refs['invoice-master-send'].adjustAndSend()
      }
      this.onEInvoiceMessageClose()
    },
    handleMarkAsSent() {
      if (this.invoice.peppolinvoice && this.invoice.peppolinvoice.status) {
        this._handlePeppolSelection(null, true)
      } else {
        if (this.stage === 'view') {
          if (this.isUploaded) {
            console.log('GETS HERE')
            this.$refs['uploaded-invoice-master-view'].markAsSent()
          } else {
            this.$refs['invoice-master-view'].markAsSent()
          }
        } else {
          this.$refs['invoice-master-send'].markAsSent()
        }
      }
    },
    handleAdjustAndSend(params) {
      if (this.invoice.peppolinvoice && this.invoice.peppolinvoice.status) {
        this._handlePeppolSelection(params, false)
      } else {
        this.$refs['invoice-master-send'].adjustAndSend()
      }
    },
    toggleRecurrentModal() {
      if (this.invoice.peppolinvoice && this.invoice.peppolinvoice.status) {
        this.showEInvoiceMessageModal = true
        this.eInvoiceShowAnimation = 'lock'
        this.eInvoiceMessageTitle = this.$gettext('E-invoice scheduling not available')
        this.eInvoiceMessageText = this.$gettext('Scheduling recurrent invoices is not available when sending electronic invoices via Peppol. This feature will be available soon. Please disable Peppol in your invoice if you want to use this feature.')
      } else {
        this.$store.commit('invoicesRecurrent/TOGGLE_MODAL')
      }
    },
    verifyInvoice(item) {
      const payload = { 
        is_verified: !item.is_verified, 
        recipient_customer: this.invoice.recipient_customer.id,
        invoice_lines: this.invoice.invoice_lines,
        invoice_date: this.invoice.invoice_date,
        payment_term: this.invoice.payment_term
      }

      InvoiceService.partialUpdate(item.id, payload)
        .then((response) => {
          item.is_verified = response.data.is_verified
          this.setListRefresh()
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: this.$gettext('Unable to verify the invoice. Please try again later.'),
            variant: 'danger',
            duration: 4,
          })
        })
    },
  }
}
</script>

<style lang="scss">
.invoice-master-modal {
  .master-invoice-modal-view-wrap {
    width: 100%;
    height: 100%;
    position: relative;
    min-height: 100%;
    // overflow: hidden;
    .master-invoice-modal-view {
      height: 100%;
      display: block;
      .view-wrap {
        height: 100%;
      }
    }
  }
}
.modal-correction {
  .modal-titlebar {
    background: $colorBrown200;
  }
}
.invoice-nav-btns-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  @media (min-width: '576px') {
    width: 50%;
  }
  @media (min-width: '768px') {
    flex: 1;
  }
}
.bottom-action-btns--wrap {
  .recurrent-summary {
    flex: 1;
    text-align: center;
    color: $colorBlue;
    cursor: pointer;
  }
}

</style>

<style lang="scss" scoped>

</style>